import { create } from "zustand";
import { IAny } from "@/types/breeze/Any";

// Define the marker interface
export interface MapMarker {
  id: number;
  lat: number;
  lng: number;
  title: string;
  description?: string;
  data: IAny;
}

// Define the map state interface
interface MapState {
  isScriptLoaded: boolean;
  markers: MapMarker[];

  // Actions
  setScriptLoaded: (loaded: boolean) => void;
  setMarkers: (markers: MapMarker[]) => void;
  processEntityData: (data: IAny[]) => void;
}

/**
 * Extract coordinates from geo data in [lat, lng] format
 */
const extractCoordinates = (geo: unknown): [number, number] | null => {
  // Handle array format
  if (Array.isArray(geo) && geo.length === 2) {
    const [lat, lng] = geo.map(Number);
    if (!isNaN(lat) && !isNaN(lng)) return [lat, lng];
  }
  // Handle string format "[51.508, -0.128]"
  else if (typeof geo === "string" && geo.includes(",")) {
    const parts = geo
      .replace(/[\[\]()]/g, "")
      .split(",")
      .map((s) => Number(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return parts as [number, number];
    }
  }
  return null;
};

const GEO_SKIP_FIELDS = new Set([
  "id", "created_at", "updated_at", "locked", "tenant_id",
  "__name", "__entity", "__url", "_formatted", "_geo",
]);

const findGeoField = (item: IAny): string | null => {
  for (const [key, value] of Object.entries(item)) {
    if (GEO_SKIP_FIELDS.has(key) || value == null) continue;
    if (extractCoordinates(value) !== null) return key;
  }
  return null;
};

// Create the map store
const useMapStore = create<MapState>((set) => ({
  isScriptLoaded: false,
  markers: [],

  // Set script loaded state
  setScriptLoaded: (loaded: boolean) => set({ isScriptLoaded: loaded }),

  // Set markers directly
  setMarkers: (markers: MapMarker[]) => set({ markers }),

  // Process entity data to extract geo information
  processEntityData: (data: IAny[]) => {
    if (!data || data.length === 0) {
      set({ markers: [] });
      return;
    }

    // Find the geo field name dynamically from the first item that has geo-like data
    const geoFieldName = findGeoField(data[0]);

    const markers = data
      .filter((item) => item.id && geoFieldName && item[geoFieldName])
      .map((item) => {
        const coords = extractCoordinates(item[geoFieldName!]);
        if (!coords) return null;

        return {
          id: item.id,
          lat: coords[0],
          lng: coords[1],
          title: String(item.name || item.title || `Item ${item.id}`),
          description: item.description ? String(item.description) : undefined,
          data: item,
        };
      })
      .filter(Boolean) as MapMarker[];

    set({ markers });
  },
}));

export default useMapStore;
